import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NOTIFICATIONS_TYPES } from 'infrastructure/notification/constants';
import {
  CommercialStateNotificationData,
  ProjectNotificationType,
  PROJECT_NOTIFICATION_CONFIRM_ACTIONS,
} from './types';
import useDismissNotifications from './useDismissNotifications';
import { getNotificationProjectId } from './notificationUtils';
import { PROJECT_NOTIFICATION_ENTITY_TYPE } from './constants';

type NotificationsRootState = { notifications: Record<string, unknown> };

export const PROJECT_NOTIFICATION_SCOPE = {
  COMMERCIAL_STATE: 'COMMERCIAL_STATE',
  STAKEHOLDERS: 'STAKEHOLDERS',
  ASSUMPTIONS: 'ASSUMPTIONS',
  ENGINEERING: 'ENGINEERING',
} as const;

type ProjectNotificationScope = (typeof PROJECT_NOTIFICATION_SCOPE)[keyof typeof PROJECT_NOTIFICATION_SCOPE];

const matchesScope = (notification: ProjectNotificationType, scope: ProjectNotificationScope): boolean => {
  const entityType = notification.metadata?.entityType;
  const confirmAction = notification.metadata?.confirmAction;

  switch (scope) {
    case PROJECT_NOTIFICATION_SCOPE.STAKEHOLDERS:
      return confirmAction === PROJECT_NOTIFICATION_CONFIRM_ACTIONS.CONFIRM_STAKEHOLDER_LIST;
    case PROJECT_NOTIFICATION_SCOPE.ASSUMPTIONS:
      return entityType === PROJECT_NOTIFICATION_ENTITY_TYPE.ASSUMPTION;
    case PROJECT_NOTIFICATION_SCOPE.ENGINEERING:
      return entityType === PROJECT_NOTIFICATION_ENTITY_TYPE.ENGINEERING;
    case PROJECT_NOTIFICATION_SCOPE.COMMERCIAL_STATE:
    default:
      return (
        confirmAction !== PROJECT_NOTIFICATION_CONFIRM_ACTIONS.CONFIRM_STAKEHOLDER_LIST &&
        entityType !== PROJECT_NOTIFICATION_ENTITY_TYPE.ASSUMPTION &&
        entityType !== PROJECT_NOTIFICATION_ENTITY_TYPE.ENGINEERING
      );
  }
};

const useReconcileProjectNotifications = () => {
  const dismissNotifications = useDismissNotifications();

  const notificationData = useSelector(
    (state: NotificationsRootState) =>
      state.notifications[NOTIFICATIONS_TYPES.COMMERCIAL_STATE] as CommercialStateNotificationData | undefined,
  );

  return useCallback(
    async (projectId: string, satisfiedScopes: ProjectNotificationScope[], entityId?: string) => {
      if (satisfiedScopes.length === 0) return;

      const list: ProjectNotificationType[] = Array.isArray(notificationData?.items) ? notificationData!.items : [];
      const ids = list
        .filter(
          notification =>
            getNotificationProjectId(notification) === projectId &&
            satisfiedScopes.some(scope => matchesScope(notification, scope)) &&
            (entityId === undefined || notification.metadata?.entityId === entityId),
        )
        .map(notification => notification.id);

      await dismissNotifications(ids);
    },
    [notificationData, dismissNotifications],
  );
};

export default useReconcileProjectNotifications;
